import { useState, useEffect } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { STRIPE_PUBLISHABLE_KEY } from "@/constants/stripe";
import { creditsService, type PricingTier } from "@/services/creditsService";

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: PricingTier;
  onSuccess: () => void;
}

export function PaymentModal({ open, onOpenChange, tier, onSuccess }: PaymentModalProps) {
  const { toast } = useToast();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loading, setLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  const [cardName, setCardName] = useState("");

  useEffect(() => {
    const initStripe = async () => {
      const stripeInstance = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      setStripe(stripeInstance);
    };
    if (open) {
      initStripe();
    }
  }, [open]);

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + " / " + v.substring(2, 4);
    }
    return v;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !tier.id) {
      toast({
        title: "Payment Error",
        description: "Stripe is not initialized or tier information is missing.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // For client-side only: Simulate payment processing
      // In a real scenario, you would:
      // 1. Create a Payment Method with Stripe
      // 2. Create a Payment Intent or Subscription
      // 3. Confirm the payment
      // Since we're client-side only, we'll process the payment directly
      
      // Validate card number (basic validation)
      const cardNumberClean = cardNumber.replace(/\s/g, "");
      if (cardNumberClean.length < 13 || cardNumberClean.length > 19) {
        throw new Error("Invalid card number");
      }

      // Simulate payment processing delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Process payment success - update credits
      // Use numberOfTokens (credits from plan) or fallback to monthlyCredits
      const creditsAmount = tier.numberOfTokens || tier.monthlyCredits;
      await creditsService.processPaymentSuccess(
        `payment_${Date.now()}`,
        tier.id!,
        creditsAmount
      );

      toast({
        title: "Payment Successful!",
        description: `Your subscription has been upgraded. ${creditsAmount.toLocaleString()} credits have been added to your account.`,
      });

      onSuccess();
      onOpenChange(false);
      
      // Reset form
      setCardNumber("");
      setCardExpiry("");
      setCardCvc("");
      setCardName("");
    } catch (error: any) {
      console.error("Payment error:", error);
      toast({
        title: "Payment Failed",
        description: error.message || "There was an error processing your payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Complete Payment</DialogTitle>
          <DialogDescription>
            Upgrade to {tier.name} plan for ${tier.price.toFixed(2)}/{tier.interval || "month"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="card-name">Cardholder Name</Label>
            <Input
              id="card-name"
              placeholder="John Doe"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="card-number">Card Number</Label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="card-number"
                className="pl-10"
                placeholder="1234 5678 9012 3456"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                maxLength={19}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="card-expiry">Expiry Date</Label>
              <Input
                id="card-expiry"
                placeholder="MM / YY"
                value={cardExpiry}
                onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                maxLength={7}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="card-cvc">CVC</Label>
              <Input
                id="card-cvc"
                placeholder="123"
                value={cardCvc}
                onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                maxLength={4}
                required
              />
            </div>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total</span>
              <span className="font-semibold">
                ${tier.price.toFixed(2)} {tier.interval && `per ${tier.interval}`}
              </span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-muted-foreground">Credits</span>
              <span className="font-semibold text-primary">
                {tier.monthlyCredits.toLocaleString()} credits
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                `Pay $${tier.price.toFixed(2)}`
              )}
            </Button>
          </DialogFooter>
        </form>

        <div className="text-xs text-muted-foreground text-center pt-2">
          <p>🔒 Secure payment processing. Your card details are handled securely.</p>
          <p className="mt-1">For testing, use card: 4242 4242 4242 4242</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}






